import { Component } from '@theme/component';

/**
 * Multi-step customer support form with routing, deflection, and capture.
 *
 * Steps: 1=user-type, 2=topic, 3=deflection (conditional), 4=capture, 5=redirect
 *
 * @typedef {object} SupportFormRefs
 * @property {HTMLElement[]} steps - Step containers
 * @property {HTMLElement[]} userTypeCards - User type selection cards
 * @property {HTMLElement[]} topicCards - Topic selection cards
 * @property {HTMLElement} deflectionTitle - Deflection article title
 * @property {HTMLElement} deflectionSummary - Deflection article summary (richtext HTML)
 * @property {HTMLElement} deflectionCta - Deflection CTA link
 * @property {HTMLElement} deflectionLink - Read more link
 * @property {HTMLElement} captureFields - Container for conditional capture fields
 * @property {HTMLInputElement} hiddenUserType - Hidden field for user type
 * @property {HTMLInputElement} hiddenTopic - Hidden field for topic
 * @property {HTMLTextAreaElement} bodyField - The message textarea
 * @property {HTMLElement} redirectMessage - Redirect message container
 * @property {HTMLElement} [breadcrumb] - Breadcrumb nav container
 * @property {HTMLButtonElement} [breadcrumbUser] - Breadcrumb user type label
 * @property {HTMLElement} [breadcrumbSep] - Breadcrumb separator
 * @property {HTMLElement} [breadcrumbTopic] - Breadcrumb topic label
 * @extends Component<SupportFormRefs>
 */
class CustomerSupportForm extends Component {
  requiredRefs = ['steps'];

  /** @type {number} */
  #currentStep = 1;

  /** @type {number[]} */
  #stepHistory = [];

  /** @type {string} */
  #userType = '';

  /** @type {string} */
  #userTypeLabel = '';

  /** @type {string} */
  #topic = '';

  /** @type {string} */
  #topicLabel = '';

  /** @type {string} */
  #originalBody = '';

  /** @type {string} */
  #lastPrefixedValue = '';

  connectedCallback() {
    super.connectedCallback();
    this.#revealStep(1);
    this.#bindForm();
  }

  updatedCallback() {
    super.updatedCallback?.();
    this.#bindForm();
  }

  #bindForm() {
    const form = this.querySelector('form');
    if (!form || form.dataset.bgSupportBound) return;
    form.dataset.bgSupportBound = 'true';
    form.addEventListener('submit', () => this.#showSending());
  }

  #showSending() {
    const button = this.querySelector('.bg-support-form__submit');
    if (!button) return;
    button.classList.add('bg-support-form__submit--loading');
    if (button.dataset.sendingLabel) button.textContent = button.dataset.sendingLabel;
  }

  handleUserType(event) {
    const card = event.target.closest('[data-user-type]');
    if (!card) return;
    this.#userType = card.dataset.userType;
    this.#userTypeLabel = card.dataset.userTypeLabel || this.#capitalize(this.#userType);

    this.refs.hiddenUserType.value = this.#userType;

    for (const tc of this.refs.topicCards ?? []) {
      const types = (tc.dataset.userTypes || '').split(',');
      tc.hidden = !types.includes(this.#userType);
    }

    this.#showStep(2);
  }

  handleTopic(event) {
    const card = event.target.closest('[data-topic]');
    if (!card) return;
    this.#topic = card.dataset.topic;
    this.#topicLabel = card.dataset.topicLabel || this.#topic;

    this.refs.hiddenTopic.value = this.#topicLabel;

    const action = card.dataset.action;
    const captureFieldTypes = (card.dataset.captureFields || '').split(',').filter(Boolean);

    if (action === 'redirect') {
      this.#showRedirect(card.dataset.redirectMessage || '');
      return;
    }

    if (action === 'deflect' || action === 'partial') {
      this.#showDeflection(card);
      return;
    }

    this.#showCapture(captureFieldTypes);
  }

  handleStillNeedHelp() {
    const activeTopicCard = this.#getActiveTopicCard();
    const captureFieldTypes = activeTopicCard
      ? (activeTopicCard.dataset.captureFields || '').split(',').filter(Boolean)
      : [];

    this.#showCapture(captureFieldTypes);
  }

  handleBack() {
    const prev = this.#stepHistory.pop();
    if (prev !== undefined) {
      this.#revealStep(prev);
    }
  }

  handleBreadcrumbUser() {
    if (this.#currentStep <= 2) return;
    this.#stepHistory = [1];
    this.#topic = '';
    this.#topicLabel = '';
    this.#revealStep(2);
  }

  handleSubmit() {
    const body = this.refs.bodyField;
    if (!body) return;

    if (body.value !== this.#lastPrefixedValue) {
      this.#originalBody = body.value;
    }

    const userLabel = this.#userTypeLabel || this.#capitalize(this.#userType);
    const prefix = `[${userLabel} > ${this.#topicLabel}]`;
    const conditionalFields = this.#gatherConditionalFields();
    body.value = conditionalFields
      ? `${prefix}\n${conditionalFields}\n\n${this.#originalBody}`
      : `${prefix}\n\n${this.#originalBody}`;
    this.#lastPrefixedValue = body.value;
  }

  #showStep(step) {
    this.#stepHistory.push(this.#currentStep);
    this.#revealStep(step);
  }

  #revealStep(step) {
    this.#currentStep = step;

    for (const s of this.refs.steps) {
      const isActive = parseInt(s.dataset.step, 10) === step;
      s.hidden = !isActive;

      if (isActive) {
        s.classList.remove('bg-support-form__step--enter');
        void s.offsetWidth;
        s.classList.add('bg-support-form__step--enter');
      }
    }

    this.#updateBreadcrumb();
  }

  #updateBreadcrumb() {
    const breadcrumb = this.refs.breadcrumb;
    if (!breadcrumb) return;

    if (this.#currentStep === 1) {
      breadcrumb.hidden = true;
      return;
    }

    breadcrumb.hidden = false;

    const showTopic = this.#currentStep >= 3 && Boolean(this.#topicLabel);
    const currentClass = 'bg-support-form__breadcrumb-item--current';
    const linkClass = 'bg-support-form__breadcrumb-link';

    if (this.refs.breadcrumbUser) {
      this.refs.breadcrumbUser.textContent = this.#userTypeLabel;
      this.refs.breadcrumbUser.classList.toggle(currentClass, !showTopic);
      this.refs.breadcrumbUser.classList.toggle(linkClass, showTopic);
      this.refs.breadcrumbUser.disabled = !showTopic;
      if (showTopic) {
        this.refs.breadcrumbUser.removeAttribute('aria-current');
      } else {
        this.refs.breadcrumbUser.setAttribute('aria-current', 'step');
      }
    }

    if (this.refs.breadcrumbSep) {
      this.refs.breadcrumbSep.hidden = !showTopic;
    }

    if (this.refs.breadcrumbTopic) {
      this.refs.breadcrumbTopic.hidden = !showTopic;
      this.refs.breadcrumbTopic.textContent = showTopic ? this.#topicLabel : '';
      this.refs.breadcrumbTopic.classList.toggle(currentClass, showTopic);
      if (showTopic) {
        this.refs.breadcrumbTopic.setAttribute('aria-current', 'step');
      } else {
        this.refs.breadcrumbTopic.removeAttribute('aria-current');
      }
    }
  }

  #capitalize(value) {
    return value ? value.charAt(0).toUpperCase() + value.slice(1) : '';
  }

  #showDeflection(topicCard) {
    const title = topicCard.dataset.deflectTitle || '';
    const summary = topicCard.dataset.deflectSummary || '';
    const ctaText = topicCard.dataset.deflectCtaText || '';
    const ctaUrl = topicCard.dataset.deflectCtaUrl || '';
    const articleUrl = topicCard.dataset.deflectArticleUrl || '';

    if (this.refs.deflectionTitle) {
      this.refs.deflectionTitle.textContent = title;
    }

    if (this.refs.deflectionSummary) {
      this.refs.deflectionSummary.innerHTML = summary;
    }

    if (this.refs.deflectionCta) {
      if (ctaText && ctaUrl) {
        this.refs.deflectionCta.textContent = ctaText;
        this.refs.deflectionCta.href = ctaUrl;
        this.refs.deflectionCta.hidden = false;
      } else {
        this.refs.deflectionCta.hidden = true;
      }
    }

    if (this.refs.deflectionLink) {
      if (articleUrl) {
        this.refs.deflectionLink.href = articleUrl;
        this.refs.deflectionLink.hidden = false;
      } else {
        this.refs.deflectionLink.hidden = true;
      }
    }

    this.#showStep(3);
  }

  #showRedirect(message) {
    if (this.refs.redirectMessage) {
      this.refs.redirectMessage.textContent = message;
    }

    this.#showStep(5);
  }

  #showCapture(fieldTypes) {
    const allConditional = this.refs.captureFields?.querySelectorAll('[data-field]') ?? [];

    for (const field of allConditional) {
      const shouldShow = fieldTypes.includes(field.dataset.field);
      field.hidden = !shouldShow;
      if (!shouldShow) {
        const input = field.querySelector('input, select, textarea');
        if (input) input.value = '';
      }
    }

    this.#showStep(4);
  }

  #getActiveTopicCard() {
    for (const tc of this.refs.topicCards ?? []) {
      if (tc.dataset.topic === this.#topic) return tc;
    }
    return null;
  }

  #gatherConditionalFields() {
    const allConditional = this.refs.captureFields?.querySelectorAll('[data-field]:not([hidden])') ?? [];
    const parts = [];

    for (const wrapper of allConditional) {
      const input = wrapper.querySelector('input, select, textarea');
      if (input && input.value.trim()) {
        const label = wrapper.querySelector('label')?.textContent?.trim() || input.name;
        parts.push(`${label}: ${input.value.trim()}`);
      }
    }

    return parts.length > 0 ? parts.join('\n') : '';
  }
}

if (!customElements.get('bg-customer-support-form')) {
  customElements.define('bg-customer-support-form', CustomerSupportForm);
}
